import {
    Flex,
    Button,
    Icon,
    Text,
    useDisclosure,
} from "@smile2impress/components/relaunch";
import { CalendarSmallIcon, CloseIcon } from "@smile2impress/components";
import { FC, useEffect } from "react";
import { useGoToBooking } from "hooks/useGoToBooking";

import { MessengerWidget } from "components/Relaunch/MessengerWidget";
import Link from "components/Link";

import { AnimatedBurger } from "../AnimatedBurger";
import { ButtonsGroupProps } from "./ButtonsGroup.types";

export const ButtonsGroup: FC<ButtonsGroupProps> = ({
    cta,
    ctaExpanded,
    contacts,
    onToggle,
    isOpen,
    isExpanded,
    testIds,
    expandableMessengerButton,
}) => {
    const { isOpen: isMessengerMenuOpen, onClose: onMessengerMenuClose } =
        useDisclosure();
    const { goToBooking } = useGoToBooking();
    if (!cta.onClick && !cta.link) {
        cta.onClick = goToBooking;
    }
    if (!ctaExpanded.onClick && !ctaExpanded.link) {
        ctaExpanded.onClick = goToBooking;
    }

    useEffect(() => {
        if (!isExpanded) onMessengerMenuClose();
    }, [isExpanded]);

    const expandMessenger = expandableMessengerButton && contacts?.wa;

    const onToggleWrapper = () => {
        onMessengerMenuClose();
        onToggle();
    };

    const CTAWrapper = () => {
        onMessengerMenuClose();
        if (cta.onClick) cta.onClick();
    };

    const collapsedStyles = {
        maxW: "0px",
        px: 0,
        opacity: 0,
    };

    const expandedStyles = {
        maxW: "100500px",
        px: "4px",
        opacity: 1,
    };

    const transition = "all .2s ease-in";

    return (
        <Flex
            direction="row"
            minWidth={4}
            h={4}
            justify="flex-end"
            position="relative"
        >
            <Flex
                basis={isOpen ? "100%" : 0}
                transition={transition}
                pr="2px"
                position="relative"
                zIndex="sticky"
            >
                <Button
                    as={Link}
                    onClick={CTAWrapper}
                    {...(cta.link ? { href: cta.link } : {})}
                    {...(isOpen ? expandedStyles : collapsedStyles)}
                    variant="red"
                    overflow="hidden"
                    width="100%"
                    h={[4, 4]}
                    ml={0}
                    px={isOpen ? 2 : 0}
                    transition={transition}
                    {...(testIds ? { "data-testid": testIds.firstCTA } : {})}
                >
                    {ctaExpanded.text}
                    {ctaExpanded.iconRight && (
                        <Icon
                            as={ctaExpanded.iconRight}
                            verticalAlign="baseline"
                            ml=".3em"
                            color="baseInverse"
                            h={[1]}
                            w={[1]}
                        />
                    )}
                </Button>
            </Flex>
            <Flex
                direction="row"
                bgColor="base.blue"
                borderRadius={"56px"}
                minWidth={[4]}
                h={[4, 4]}
                px={isExpanded && !isOpen ? "10px" : 0}
                transition={transition}
                boxShadow={"medium"}
                position="relative"
                zIndex="sticky"
                alignItems="center"
            >
                <Button
                    variant="ghost"
                    h={[4, 4]}
                    px={isExpanded && !isOpen ? ["4px"] : ["10px"]}
                    onClick={onToggleWrapper}
                    {...(testIds ? { "data-testid": testIds.hamburger } : {})}
                >
                    {isOpen ? (
                        <Icon
                            as={CloseIcon}
                            w="36px"
                            h="36px"
                            color="text.baseInverse"
                            _dark={{ color: "text.base" }}
                        />
                    ) : (
                        <AnimatedBurger />
                    )}
                </Button>
                <Button
                    as={Link}
                    variant="ghost"
                    h={[4, 4]}
                    overflow="hidden"
                    transition={transition}
                    color="base.white"
                    {...(isExpanded && !isOpen
                        ? expandedStyles
                        : collapsedStyles)}
                    onClick={CTAWrapper}
                    {...(cta.link ? { href: cta.link } : {})}
                    {...(testIds ? { "data-testid": testIds.secondCTA } : {})}
                    {...(expandableMessengerButton && {
                        minWidth: isMessengerMenuOpen ? "150px" : 0,
                    })}
                >
                    <Flex
                        as="span"
                        pointerEvents="none"
                        h={3}
                        py="4px"
                        px={1}
                        bg="base.halfBlack"
                        borderRadius="35px"
                        alignItems="center"
                    >
                        <Icon
                            as={CalendarSmallIcon}
                            w="24px"
                            h="24px"
                            color="text.baseInverse"
                            _dark={{ color: "text.base" }}
                        />
                        <Text
                            textStyle={"body"}
                            color="text.baseInverse"
                            ml={0.5}
                            _dark={{ color: "text.base" }}
                        >
                            {cta.text}
                        </Text>
                    </Flex>
                </Button>
                {isExpanded && !isOpen && (
                    <MessengerWidget
                        expandableMessengerButton={!!expandMessenger}
                        isBuiltIn={true}
                    />
                )}
            </Flex>
        </Flex>
    );
};
